'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useLocale, useTranslations } from 'next-intl';

import { Accordion } from '@/components/ui/Accordion';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { SectionLoadingOverlay } from '@/components/ui/SectionLoadingOverlay';
import { fetchServiceIdentifyWatch } from '@/lib/myWatches/identifyClient';
import type { Watch } from '@/lib/myWatches/types';
import { WarrantyStatus } from '@/lib/myWatches/types';
import type {
  RepairDetails,
  RepairReasonResponse,
  WatchProductSummary,
} from '@/lib/service/ecs/contracts';
import {
  fetchAuthenticatedRepairList,
  fetchGuestRepairList,
} from '@/lib/service/repairDetailsClient';
import {
  buildRepairUpdateRequest,
  type BuildRepairUpdateRequestInput,
} from '@/lib/service/repairDetailsUpdate';
import { updateRepairDetails } from '@/lib/service/repairDetailsUpdateClient';
import { fetchRepairReasons } from '@/lib/service/repairReasonClient';
import type { WarrantyExtensionOffer } from '@/lib/service/repairDetailsSidebarEligibility';
import { fetchWarrantyExtensionProductPrice } from '@/lib/service/warrantyExtensionProductClient';
import type { RepairDetailsInitialData } from '@/lib/service/repairDetailsInitialData';
import {
  readPrimaryRepairItem,
  repairNumberForAccountLink,
  selectRequestedRepairs,
} from '@/lib/service/repairDetailsInitialData';
import { fetchWatchDetails } from '@/lib/service/watchDetailsClient';

import { RepairDetailsRepairPanel } from './RepairDetailsRepairPanel';
import { RepairDetailsShell } from './RepairDetailsShell';
import { RepairDetailsWatchSidebar } from './RepairDetailsWatchSidebar';
import styles from './RepairDetailsView.module.scss';

interface RepairDetailsViewProps {
  countryCode: string;
  email: string;
  accessCode?: string;
  watchSerial?: string;
  loginRequired?: boolean;
  isAuthenticated?: boolean;
  ecsAvailable?: boolean;
  initialData?: RepairDetailsInitialData | null;
  /** Legacy base64 Service Details payload preserved for quote return navigation. */
  returnContext?: string;
}

async function loadWatchSidebarData({
  serial,
  reference,
  locale,
  countryCode,
  setWatchWarranty,
  setWarrantyCode,
  setProductSummary,
  setWarrantyExtensionOffer,
}: {
  serial: string;
  reference: string;
  locale: string;
  countryCode: string;
  setWatchWarranty: Dispatch<SetStateAction<Watch | null>>;
  setWarrantyCode: Dispatch<SetStateAction<number | null>>;
  setProductSummary: Dispatch<SetStateAction<WatchProductSummary | null>>;
  setWarrantyExtensionOffer: Dispatch<SetStateAction<WarrantyExtensionOffer | null>>;
}): Promise<void> {
  if (!serial) return;

  setWarrantyCode(null);
  setWarrantyExtensionOffer(null);

  const identifyResult = await fetchServiceIdentifyWatch({ sn: serial, locale });
  const identifyCode = identifyResult?.code ?? null;
  setWarrantyCode(identifyCode);

  if (identifyResult?.data && identifyCode !== WarrantyStatus.NotFound && identifyResult.data.sku) {
    setWatchWarranty(identifyResult.data);
  } else {
    setWatchWarranty(null);
  }

  const watchReference = reference || identifyResult?.data?.sku;
  if (!watchReference) {
    setProductSummary(null);
    return;
  }

  try {
    const summary = await fetchWatchDetails({
      countryCode,
      locale,
      watchReference,
    });
    setProductSummary(summary);

    if (identifyCode === WarrantyStatus.RegisteredBySelf && summary.warrantyExtensionDuration) {
      const price = await fetchWarrantyExtensionProductPrice(locale);
      if (price) {
        setWarrantyExtensionOffer({
          duration: summary.warrantyExtensionDuration,
          price,
        });
      }
    }
  } catch {
    setProductSummary(null);
  }
}

function hasGuestLookup(email: string, accessCode?: string): boolean {
  return Boolean(accessCode && email);
}

function hasAuthenticatedLookup(accessCode?: string, watchSerial?: string): boolean {
  return Boolean(accessCode || watchSerial);
}

export function RepairDetailsView({
  countryCode,
  email,
  accessCode,
  watchSerial,
  loginRequired = false,
  isAuthenticated = false,
  ecsAvailable = false,
  initialData = null,
  returnContext,
}: Readonly<RepairDetailsViewProps>) {
  const locale = useLocale();
  const t = useTranslations('service.repairDetails');
  const tCommon = useTranslations('common');
  const showWatchSidebar = loginRequired || isAuthenticated;
  const accessErrorMessage = t('accessError');
  const maintenanceServiceLabel = t('maintenanceService');
  const hasServerInitialData = initialData != null;
  const repairNumberFilter = repairNumberForAccountLink(loginRequired, accessCode);

  const [repairs, setRepairs] = useState<RepairDetails[]>(() =>
    selectRequestedRepairs(initialData?.repairs, repairNumberFilter)
  );
  const [reasonCatalogue, setReasonCatalogue] = useState<RepairReasonResponse['items']>(
    initialData?.reasonCatalogue
  );
  const [productSummary, setProductSummary] = useState<WatchProductSummary | null>(
    initialData?.watchSidebar?.productSummary ?? null
  );
  const [watchWarranty, setWatchWarranty] = useState<Watch | null>(
    initialData?.watchSidebar?.watchWarranty ?? null
  );
  const [warrantyCode, setWarrantyCode] = useState<number | null>(
    initialData?.watchSidebar?.warrantyCode ?? null
  );
  const [warrantyExtensionOffer, setWarrantyExtensionOffer] =
    useState<WarrantyExtensionOffer | null>(
      initialData?.watchSidebar?.warrantyExtensionOffer ?? null
    );
  const shouldLoadInitially = loginRequired
    ? hasAuthenticatedLookup(accessCode, watchSerial)
    : hasGuestLookup(email, accessCode);
  const [isLoading, setIsLoading] = useState(hasServerInitialData ? false : shouldLoadInitially);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [isLastPage, setIsLastPage] = useState(initialData?.isLastPage ?? true);
  const pageRef = useRef(0);

  const guestAccess = useMemo(
    () => (!loginRequired && accessCode ? { email, accessCode } : undefined),
    [accessCode, email, loginRequired]
  );

  const primaryItem = readPrimaryRepairItem(repairs[0]);
  const referenceNumber = primaryItem?.reference ?? '';
  const serialNumber = primaryItem?.serial ?? watchSerial ?? '';

  const handleRepairUpdate = useCallback(
    async (repair: RepairDetails, input: Omit<BuildRepairUpdateRequestInput, 'repair'>) => {
      setIsUpdating(true);
      setAccessError(null);
      try {
        await updateRepairDetails({
          countryCode,
          request: buildRepairUpdateRequest({ repair, ...input }),
        });
        pageRef.current = 0;
        const repairList = loginRequired
          ? await fetchAuthenticatedRepairList({
              countryCode,
              ...(accessCode ? { repairNumber: accessCode } : {}),
              ...(watchSerial ? { watchSerial } : {}),
            })
          : await fetchGuestRepairList({
              countryCode,
              email,
              accessCode: accessCode!,
            });
        setRepairs(selectRequestedRepairs(repairList.items, repairNumberFilter));
        setIsLastPage(loginRequired && accessCode ? true : repairList.isLast);
      } catch {
        setAccessError(accessErrorMessage);
      } finally {
        setIsUpdating(false);
      }
    },
    [
      accessCode,
      accessErrorMessage,
      countryCode,
      email,
      loginRequired,
      repairNumberFilter,
      watchSerial,
    ]
  );

  const accordionItems = useMemo(
    () =>
      repairs.map((repair) => ({
        id: repair.repairNumber,
        title: maintenanceServiceLabel,
        content: (
          <RepairDetailsRepairPanel
            repair={repair}
            reasonCatalogue={reasonCatalogue}
            isUpdating={isUpdating}
            returnContext={returnContext}
            guestAccess={guestAccess}
            onUpdate={(input) => handleRepairUpdate(repair, input)}
          />
        ),
      })),
    [
      guestAccess,
      handleRepairUpdate,
      isUpdating,
      maintenanceServiceLabel,
      reasonCatalogue,
      repairs,
      returnContext,
    ]
  );

  useEffect(() => {
    if (hasServerInitialData) return;

    const shouldLoad = loginRequired
      ? hasAuthenticatedLookup(accessCode, watchSerial)
      : hasGuestLookup(email, accessCode);

    if (!shouldLoad) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function refreshWatchSidebarData(serial: string, reference: string) {
      if (cancelled) return;
      await loadWatchSidebarData({
        serial,
        reference,
        locale,
        countryCode,
        setWatchWarranty,
        setWarrantyCode,
        setProductSummary,
        setWarrantyExtensionOffer,
      });
    }

    async function loadInitialData() {
      setIsLoading(true);
      setAccessError(null);
      pageRef.current = 0;
      setWatchWarranty(null);
      setWarrantyCode(null);
      setWarrantyExtensionOffer(null);
      setProductSummary(null);

      try {
        const repairListPromise = loginRequired
          ? fetchAuthenticatedRepairList({
              countryCode,
              ...(accessCode ? { repairNumber: accessCode } : {}),
              ...(watchSerial ? { watchSerial } : {}),
            })
          : fetchGuestRepairList({
              countryCode,
              email,
              accessCode: accessCode!,
            });

        const [repairList, reasons] = await Promise.all([
          repairListPromise,
          fetchRepairReasons({ countryCode, locale }),
        ]);

        if (cancelled) return;

        const items = selectRequestedRepairs(repairList.items, repairNumberFilter);
        setRepairs(items);
        setIsLastPage(loginRequired && accessCode ? true : repairList.isLast);
        setReasonCatalogue(reasons.items);

        const firstItem = readPrimaryRepairItem(items[0]);
        const shouldLoadWatchSidebar = loginRequired || isAuthenticated;
        if (shouldLoadWatchSidebar) {
          await refreshWatchSidebarData(
            firstItem?.serial ?? watchSerial ?? '',
            firstItem?.reference ?? ''
          );
        }
      } catch {
        if (!cancelled) {
          setAccessError(accessErrorMessage);
          setRepairs([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void loadInitialData();

    return () => {
      cancelled = true;
    };
  }, [
    accessCode,
    accessErrorMessage,
    countryCode,
    email,
    hasServerInitialData,
    isAuthenticated,
    locale,
    loginRequired,
    repairNumberFilter,
    watchSerial,
  ]);

  const loadMore = async () => {
    if (isLoadingMore || isLastPage) return;

    if (loginRequired) {
      if (!hasAuthenticatedLookup(accessCode, watchSerial)) return;
    } else if (!accessCode) {
      return;
    }

    setIsLoadingMore(true);
    try {
      pageRef.current += 1;
      const repairList = loginRequired
        ? await fetchAuthenticatedRepairList({
            countryCode,
            page: pageRef.current,
            ...(accessCode ? { repairNumber: accessCode } : {}),
            ...(watchSerial ? { watchSerial } : {}),
          })
        : await fetchGuestRepairList({
            countryCode,
            email,
            accessCode: accessCode!,
            page: pageRef.current,
          });
      const nextItems = selectRequestedRepairs(repairList.items, repairNumberFilter);
      setRepairs((current) => [...current, ...nextItems]);
      setIsLastPage(repairList.isLast);
    } catch {
      setAccessError(accessErrorMessage);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const refreshWatchSidebar = useCallback(async () => {
    await loadWatchSidebarData({
      serial: serialNumber,
      reference: referenceNumber,
      locale,
      countryCode,
      setWatchWarranty,
      setWarrantyCode,
      setProductSummary,
      setWarrantyExtensionOffer,
    });
  }, [countryCode, locale, referenceNumber, serialNumber]);

  const sidebar = showWatchSidebar ? (
    <RepairDetailsWatchSidebar
      referenceNumber={referenceNumber}
      serialNumber={serialNumber}
      productSummary={productSummary}
      warranty={watchWarranty}
      warrantyCode={warrantyCode}
      ecsAvailable={ecsAvailable}
      warrantyExtensionOffer={warrantyExtensionOffer}
      onWatchRegistered={refreshWatchSidebar}
    />
  ) : undefined;

  if (isLoading) {
    return (
      <SectionLoadingOverlay isLoading label={t('loading')} transparent>
        <div className={styles.loadingHost} data-testid="RepairDetailsView-loading" />
      </SectionLoadingOverlay>
    );
  }

  return (
    <RepairDetailsShell sidebar={sidebar}>
      <h2 className={styles.mainTitle} data-testid="repair-details-main-title">
        {t('title')}
      </h2>

      {accessError ? (
        <p className={styles.error} role="alert" data-testid="repair-details-access-error">
          {accessError}
        </p>
      ) : null}

      {repairs.length > 0 ? (
        <>
          <Accordion
            items={accordionItems}
            defaultOpenIds={repairs[0] ? [repairs[0].repairNumber] : []}
            headingLevel="h3"
            icon={<Icon aria-hidden="true" name="chevron-down" size={16} />}
            classNames={{
              root: styles.accordion,
              item: styles.accordionItem,
              trigger: styles.accordionTrigger,
              title: styles.accordionTitle,
              panel: styles.accordionPanel,
              panelInner: styles.accordionPanelInner,
            }}
            testIdPrefix="repair-details"
          />

          {!isLastPage ? (
            <div className={styles.moreResultsRow}>
              <Button
                type="button"
                variant="outline"
                onClick={() => void loadMore()}
                loading={isLoadingMore}
                disabled={isLoadingMore}
                data-testid="repair-details-more-results"
              >
                {tCommon('moreResults')}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </RepairDetailsShell>
  );
}
